import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/cn'

export type MetricRowTone = 'neutral' | 'critical' | 'pending' | 'positive'

export interface MetricRowProps {
  /** The noun. Carries the meaning — keep it a phrase, not a label. */
  label: React.ReactNode
  value: React.ReactNode
  /** Secondary line under the label. */
  hint?: React.ReactNode
  tone?: MetricRowTone
  /** Makes the whole row a link to this destination. */
  to?: string
  /** Link affordance text, announced with the label. Requires `to`. */
  actionLabel?: string
  icon?: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
  className?: string
}

/**
 * One metric as a horizontal line: label left, figure right.
 *
 * The compact alternative to a stat card. A row of these scans top-to-bottom in
 * a narrow column, so the eye never crosses the viewport to pair a number with
 * its name — which is exactly what a 4-up grid of cards forces.
 */
const toneValue: Record<MetricRowTone, string> = {
  neutral: 'text-gray-900 dark:text-gray-100',
  critical: 'text-red-700 dark:text-red-300',
  pending: 'text-amber-800 dark:text-amber-300',
  positive: 'text-teal-800 dark:text-teal-300',
}

const toneIcon: Record<MetricRowTone, string> = {
  neutral: 'text-gray-400 dark:text-gray-500',
  critical: 'text-red-600 dark:text-red-400',
  pending: 'text-amber-600 dark:text-amber-400',
  positive: 'text-teal-700 dark:text-teal-400',
}

export const MetricRow = ({
  label,
  value,
  hint,
  tone = 'neutral',
  to,
  actionLabel,
  icon: Icon,
  className,
}: MetricRowProps): React.ReactElement => {
  const body = (
    <>
      {Icon && (
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', toneIcon[tone])} aria-hidden />
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
        {hint != null && (
          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{hint}</span>
        )}
      </span>
      <span
        className={cn(
          'shrink-0 text-lg font-semibold tabular-nums tracking-[-0.01em]',
          toneValue[tone]
        )}
      >
        {value}
      </span>
      {to != null && (
        <ArrowRight
          className="h-3.5 w-3.5 shrink-0 text-gray-300 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-primary dark:text-gray-600"
          aria-hidden
        />
      )}
    </>
  )

  const shared = 'flex items-start gap-3 px-4 py-3 text-left'

  if (to == null) {
    return <div className={cn(shared, className)}>{body}</div>
  }

  return (
    <Link
      to={to}
      className={cn(
        shared,
        'group rounded-lg transition-colors duration-150',
        'hover:bg-gray-50 dark:hover:bg-gray-700/40',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-800',
        className
      )}
    >
      {body}
      {actionLabel && <span className="sr-only">{actionLabel}</span>}
    </Link>
  )
}

MetricRow.displayName = 'MetricRow'
